import { Mutex } from 'async-mutex';

import { ResultType } from '../../../main/types/result.types.ts';
import { getHashUtil } from '../../../main/utils/hash.util.ts';
import { getLocaleUtil } from '../../../main/utils/locale.util.ts';

import { CACHE_DURATION, PENDING_REQUEST_TIMEOUT } from '../../constants/speechsuper.constants.ts';
import { initializePronunciationGraderMutationRequest } from '../../requests/initialize-pronunciation-grader.mutation.ts';
import {
  GraderProvider,
  SpeechSuperRequestType,
  InitializePronunciationGraderResponse,
  SpeechSuperConnectAppParams,
  InitializePronunciationGraderInput,
  SpeechSuperStartAppParams,
  CachedParams,
  PendingRequest,
} from '../../types/pronunciation-grading-initialization.types.ts';

/**
 * Utility class to manage SpeechSuper app parameters for assessments and connections.
 * Handles caching and deduplication of requests to avoid unnecessary API calls.
 */
export class SpeechSuperAppParamsProviderUtil {
  private static _instance: SpeechSuperAppParamsProviderUtil;

  private paramsCache = new Map<string, CachedParams>();
  private pendingRequests = new Map<string, PendingRequest>();
  private mutex = new Mutex();

  static get instance(): SpeechSuperAppParamsProviderUtil {
    if (!SpeechSuperAppParamsProviderUtil._instance) {
      SpeechSuperAppParamsProviderUtil._instance = new SpeechSuperAppParamsProviderUtil();
    }
    return SpeechSuperAppParamsProviderUtil._instance;
  }

  private constructor() {
    // Private constructor to enforce singleton pattern
  }

  /**
   * Gets app parameters for a new assessment.
   * Uses caching and request deduplication to minimize API calls.
   */
  async getAssessmentParams(): Promise<SpeechSuperStartAppParams> {
    const cacheKey = this.getCacheKey('assessment');
    const request: InitializePronunciationGraderInput = {
      provider: GraderProvider.SPEECHSUPER,
      metadata: {
        requests: [
          {
            type: SpeechSuperRequestType.START,
          },
        ],
      },
    };

    return this.getParams(cacheKey, () => this.makeRequest(request));
  }

  /**
   * Gets app parameters for a new connection.
   * Uses caching and request deduplication to minimize API calls.
   */
  async getConnectionParams(): Promise<SpeechSuperConnectAppParams> {
    const cacheKey = this.getCacheKey('connection');
    const request: InitializePronunciationGraderInput = {
      provider: GraderProvider.SPEECHSUPER,
      metadata: {
        requests: [
          {
            type: SpeechSuperRequestType.CONNECT,
          },
        ],
      },
    };

    return this.getParams(cacheKey, () => this.makeRequest(request));
  }

  private async makeRequest(
    input: InitializePronunciationGraderInput
  ): Promise<ResultType<InitializePronunciationGraderResponse>> {
    const result = await initializePronunciationGraderMutationRequest({
      provider: input.provider,
      metadata: input.metadata,
    });
    if (!result.success || !result.data) {
      const errorMessage = getLocaleUtil().select('learn:pronunciation_grader_initialization_failed');
      throw new Error(errorMessage);
    }

    return {
      success: result.success,
      data: result.data,
    };
  }

  private getCacheKey(type: string, params: Record<string, unknown> = {}): string {
    return getHashUtil().hash({ type, ...params });
  }

  private async getParams<T extends SpeechSuperConnectAppParams>(
    cacheKey: string,
    requestFn: () => Promise<ResultType<InitializePronunciationGraderResponse>>
  ): Promise<T> {
    await this.mutex.acquire();
    try {
      const cachedResult = this.getFromCache(cacheKey);
      if (cachedResult) {
        return cachedResult as T;
      }

      const pendingResult = await this.getFromPendingRequests(cacheKey);
      if (pendingResult) {
        return pendingResult as T;
      }

      return this.executeAndCacheRequest(cacheKey, requestFn);
    } finally {
      this.mutex.release();
    }
  }

  private getFromCache<T extends SpeechSuperConnectAppParams>(cacheKey: string): T | null {
    const cachedParams = this.paramsCache.get(cacheKey);
    if (cachedParams && cachedParams.expiresAt > Date.now()) {
      return cachedParams.value as T;
    }

    // Clean up expired cache entry
    if (cachedParams) {
      this.paramsCache.delete(cacheKey);
    }

    return null;
  }

  private async getFromPendingRequests<T extends SpeechSuperConnectAppParams>(cacheKey: string): Promise<T | null> {
    const pendingRequest = this.pendingRequests.get(cacheKey);
    if (pendingRequest && pendingRequest.expiresAt > Date.now()) {
      try {
        const result = await pendingRequest.promise;
        return result.value as T;
      } catch {
        // If the pending request fails, clean it up and return null
        this.pendingRequests.delete(cacheKey);
      }
    }

    // Clean up expired pending request
    if (pendingRequest) {
      this.pendingRequests.delete(cacheKey);
    }

    return null;
  }

  private async executeAndCacheRequest<T extends SpeechSuperConnectAppParams>(
    cacheKey: string,
    requestFn: () => Promise<ResultType<InitializePronunciationGraderResponse>>
  ): Promise<T> {
    const promise = this.fetchAndCacheParams(cacheKey, requestFn);
    this.pendingRequests.set(cacheKey, {
      promise,
      expiresAt: Date.now() + PENDING_REQUEST_TIMEOUT,
    });

    try {
      const result = await promise;
      return result.value as T;
    } finally {
      this.pendingRequests.delete(cacheKey);
    }
  }

  private async fetchAndCacheParams(
    cacheKey: string,
    requestFn: () => Promise<ResultType<InitializePronunciationGraderResponse>>
  ): Promise<CachedParams> {
    const response = await requestFn();

    if (!response.success || !response.data || response.data.provider !== GraderProvider.SPEECHSUPER) {
      const errorMessage = getLocaleUtil().select('learn:pronunciation_grader_initialization_failed');
      throw new Error(errorMessage);
    }

    const params = response.data.metadata.responses[0];

    const cachedParams: CachedParams = {
      value: params,
      expiresAt: Date.now() + CACHE_DURATION,
    };

    this.paramsCache.set(cacheKey, cachedParams);
    return cachedParams;
  }
}

/**
 * Gets the singleton instance of SpeechSuperAppParamsProviderUtil.
 */
export const getSpeechSuperAppParamsProviderUtil = (): SpeechSuperAppParamsProviderUtil =>
  SpeechSuperAppParamsProviderUtil.instance;
